<script setup lang="ts">
/**
 * StepUpModal — singleton modal driven by {@code useStepUp()} (BRD NFR-SEC-05).
 *
 * Mounted once at the app root; subscribes to the module-level challenge
 * state from {@code composables/useStepUp.ts}. When a caller invokes
 * {@code requireStepUp(...)} the modal opens, collects a 6-digit MFA code,
 * hands it to the configured issuer, and resolves the caller's Promise with
 * the returned token.
 *
 * Factor — the default issuer expects a 6-digit TOTP/SMS code (Platform API
 * {@code MfaChallengeService} issues both). Password re-entry is not used
 * for step-up in SYNC; MFA is the second factor per the UI Spec.
 */
import { computed, ref, watch } from 'vue'
import { Banner, Button, FormField, Input, Modal } from '@kori/ui'
import { __stepUpInternal } from '@/composables/useStepUp'

const code = ref('')
const submitting = ref(false)

const challenge = __stepUpInternal.activeChallenge
const errorMessage = __stepUpInternal.lastError

const isOpen = computed<boolean>(() => challenge.value !== null)

const titleLabel = computed<string>(() =>
  challenge.value?.label
    ? `Confirm: ${challenge.value.label}`
    : 'Confirm sensitive action',
)

const actionHint = computed<string>(() => challenge.value?.action ?? '')

// Reset the local input whenever a new challenge shows up.
watch(challenge, (next, prev) => {
  if (next && next !== prev) {
    code.value = ''
    submitting.value = false
  }
})

async function submit(): Promise<void> {
  if (!/^\d{6}$/.test(code.value)) return
  submitting.value = true
  try {
    await __stepUpInternal.submit(code.value)
  } finally {
    submitting.value = false
  }
}

function cancel(): void {
  __stepUpInternal.cancel()
}

function onOpenChange(next: boolean): void {
  if (!next) cancel()
}
</script>

<template>
  <Modal
    :open="isOpen"
    :title="titleLabel"
    description="For your security, confirm this action by entering the 6-digit code from your authenticator app."
    max-width="sm"
    @update:open="onOpenChange"
  >
    <form
      class="flex flex-col gap-3"
      @submit.prevent="submit"
    >
      <Banner v-if="errorMessage" severity="danger" :message="errorMessage" />

      <p class="text-xs text-text-tertiary">
        Action: <span class="font-mono">{{ actionHint }}</span>
      </p>

      <FormField
        v-slot="{ id }"
        label="Authentication code"
        required
        helper-text="6 digits, from your TOTP app or the SMS you just received."
      >
        <Input
          :id="id"
          v-model="code"
          inputmode="numeric"
          autocomplete="one-time-code"
          maxlength="6"
          placeholder="123 456"
          :disabled="submitting"
          autofocus
        />
      </FormField>

    </form>

    <template #footer>
      <Button
        type="button"
        variant="secondary"
        :disabled="submitting"
        @click="cancel"
      >
        Cancel
      </Button>
      <Button
        type="button"
        :loading="submitting"
        :disabled="!/^\d{6}$/.test(code)"
        @click="submit"
      >
        Confirm
      </Button>
    </template>
  </Modal>
</template>
